/**
 * Debounces a "join burst" of video `newConsumer` messages down to one consume per
 * slot. Right after joining a room, the server can push several `newConsumer` messages
 * for the same camera in quick succession (e.g. a producer that was just replaced) —
 * consuming every one of them would flash through stale video before landing on the
 * current producer. Instead, each incoming message for a slot resets a short quiet-
 * window timer; only once that window passes without a newer one does the pending
 * message actually get consumed (`consume`, injected by the caller — see
 * `room-consumers.ts`'s `consumeProducer`). A lone message (no burst) still goes through
 * once its window elapses, so this also covers the ordinary "camera off → on" case, not
 * just startup floods.
 */

import { resolveConsumerSlot, type ResolveMountSlot } from "./consumer-slots";
import type {
  NewConsumerData,
  ProtooRequestMessage,
  ProtooNotificationMessage,
} from "./protoo-messages";
import type { ProtooClient } from "./protoo-client";

/** Wait for a quiet window so a join burst of stale producers collapses to the newest. */
export const VIDEO_SLOT_COALESCE_MS = 120;

/** Prefer the camera's original H.264 over its transcoded VP8 alternative. */
export function videoCodecPreference(data: NewConsumerData): number {
  const mime = data.rtpParameters?.codecs?.find((codec) =>
    codec.mimeType.toLowerCase() !== "video/rtx")?.mimeType.toLowerCase();
  return mime === "video/h264" ? 2 : mime === "video/vp8" ? 1 : 0;
}

export interface CreateVideoConsumerCoalescerDeps {
  protoo: ProtooClient;
  appendDiag: (s: string, opts?: { level?: string }) => void;
  resolveMountSlot?: ResolveMountSlot | null;
  videoSlotCoalesceMs?: number;
  /** The SAME Map instance `room-consumers.ts`'s core uses, so a slot resolved here (to
   * group the burst) and a slot resolved again during the actual consume agree — see
   * `resolveConsumerSlot`'s caching. */
  producerSlot: Map<string, number>;
  consume: (data: NewConsumerData, opts?: { onConsumed?: () => void }) => Promise<void>;
}

interface PendingVideoConsumer {
  // Only ever set from a ProtooRequestMessage's own `id` (always a number in this
  // protocol) — never a string; notifications (isRequest: false) always leave this null.
  msgId: number | null;
  data: NewConsumerData;
  isRequest: boolean;
}

export function createVideoConsumerCoalescer({
  protoo,
  appendDiag,
  resolveMountSlot = null,
  videoSlotCoalesceMs = VIDEO_SLOT_COALESCE_MS,
  producerSlot,
  consume,
}: CreateVideoConsumerCoalescerDeps) {
  /** Pending video newConsumer per slot (join burst coalesce). */
  const pendingVideoBySlot = new Map<number, PendingVideoConsumer>();
  const coalesceTimers = new Map<number, ReturnType<typeof setTimeout>>();

  function dropPendingVideo(pending: PendingVideoConsumer, reason: string) {
    appendDiag(
      `coalesce: drop stale video producer=${pending.data?.producerId ?? "?"} (${reason})`
    );
    if (pending.isRequest && pending.msgId != null) {
      protoo.sendResponse(pending.msgId, false, {}, reason, 409);
    }
  }

  function clearVideoCoalesce() {
    for (const timer of coalesceTimers.values()) {
      clearTimeout(timer);
    }
    coalesceTimers.clear();
    for (const pending of pendingVideoBySlot.values()) {
      dropPendingVideo(pending, "session reset");
    }
    pendingVideoBySlot.clear();
  }

  async function flushVideoSlot(slot: number) {
    coalesceTimers.delete(slot);
    const pending = pendingVideoBySlot.get(slot);
    pendingVideoBySlot.delete(slot);
    if (!pending) return;
    let acked = false;
    const ackOk = () => {
      if (acked || !pending.isRequest || pending.msgId == null) return;
      acked = true;
      protoo.sendResponse(pending.msgId, true, {});
    };
    try {
      await consume(pending.data, { onConsumed: ackOk });
      // Duplicate/skip paths never consume; still ACK so protoo does not hang.
      ackOk();
    } catch (e) {
      appendDiag(`consume video failed: ${e}`, { level: "warn" });
      if (!acked && pending.isRequest && pending.msgId != null) {
        protoo.sendResponse(pending.msgId, false, {}, String(e), 500);
      }
    }
  }

  /**
   * Collapse a join burst of stale video producers into a single consume/attach
   * (newest wins). Camera off→on still works: a lone newConsumer after the quiet
   * window is consumed and replaces the previous producer.
   */
  function scheduleVideoConsumer(
    msg: ProtooRequestMessage | ProtooNotificationMessage,
    isRequest: boolean
  ) {
    const data = (msg.data ?? {}) as NewConsumerData;
    const slot = resolveConsumerSlot(data, producerSlot, appendDiag, resolveMountSlot);
    const prev = pendingVideoBySlot.get(slot);
    if (prev && videoCodecPreference(prev.data) > videoCodecPreference(data)
      && videoCodecPreference(data) > 0) {
      dropPendingVideo({ data, isRequest, msgId: isRequest && "id" in msg ? msg.id : null },
        "prefer original H.264 camera");
      return;
    }
    if (prev) {
      dropPendingVideo(prev, "superseded by newer producer for same slot");
    }
    pendingVideoBySlot.set(slot, {
      msgId: isRequest && "id" in msg ? msg.id : null,
      data,
      isRequest,
    });
    const existingTimer = coalesceTimers.get(slot);
    if (existingTimer) clearTimeout(existingTimer);
    coalesceTimers.set(
      slot,
      setTimeout(() => {
        void flushVideoSlot(slot);
      }, videoSlotCoalesceMs)
    );
  }

  return { scheduleVideoConsumer, clearVideoCoalesce };
}

export type VideoConsumerCoalescer = ReturnType<typeof createVideoConsumerCoalescer>;
